import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCSS } from '../../lib/hooks';
import { injectIntl } from 'react-intl';
import { getInitials } from '../../lib/ep';

import ActionLink from '../builder/ActionLink';
import ButtonCollection from '../builder/ButtonCollection';
import Spinner from '../builder/Spinner';
import FloatingExtraOptionsMenu from './FloatingExtraOptionsMenu';
import RibbonCollection from './RibbonCollection';

const Wrapper = ({ item, ...props }) => {
	const is_self = item?.self ?? true;
	const role = item?.role ?? false;
	const display_options = item?.display_options ?? false;
	const use_route = item?.use_route ?? false;
	const full_link = item?.full_link ?? false;
	const between_icon = props?.between_icon ?? false;

	const click = () => {
		if(props.whole_action != undefined) props.whole_action(item.id, item.self);
	};

	let main_classes = ['_card', 'shadow_small', 'gui_pos_rel'];
	if(is_self) main_classes.push('self_agent');
	if(role === false) main_classes.push('without_role');
	if(props.whole_action != undefined && !is_self) main_classes.push('cursor_pointer');
	if(display_options) main_classes.push('gui_menu_opened');
	if(between_icon !== false) main_classes.push(`${between_icon} between_icon`);
	if(item.card_classes != undefined) main_classes.push(item.card_classes);

	if(use_route && full_link !== false) {
		return (
			<Link class={main_classes.join(" ")} to={full_link}>
				{props.children}
			</Link>
		)
	}

	return (
		<div class={main_classes.join(" ")} onClick={click}>
			{props.children}
		</div>
	)
};

const SingleCard = ({ item, ...props }) => {
	const [ display_options, setDisplayOptions ] = useState(false);

	const role = item?.role ?? false;
	const not_overflowed = item?.not_overflowed ?? false;
	const what_is_it_link = item?.what_is_it_link ?? false;

	return (
		<Wrapper {...props} item={item} display_options={display_options}>
			<div class="gui_pos_rel avatar_manager">
				{(item.full_image != undefined) && (
					<span style={{backgroundImage:`url(${item.full_image})`}}>
						<img src="/img/blank_1x1.png" width="100%" />
					</span>
				)}

				{(item.icon != undefined && item.full_image == undefined) && (
					<span class={`with_icon ${item.icon}`} />
				)}

				{(item.icon == undefined && item.full_image == undefined) && (
					<span class="account_initials gui_pos_abs blur_while_opened">
						{item?.initials ?? getInitials({ username:item.title })}
					</span>
				)}

				{(role !== false) && (
					<p class="role blur_while_opened">{role}</p>
				)}
			</div>
			
			<div class="_info blur_while_opened">
				<h4 class="title">{item.title}</h4>

				{(item.link != undefined) && (
					<p class="linked_item">{item.link}</p>
				)}

				{(item.link_collection != undefined) && (
					<div class="linked_item is_collection">
						{item.link_collection.map((link, l) => {
							const enabled = link?.enabled ?? true;
							if(enabled !== true) return null;

							return (
								<ActionLink
									key={`link_${props.index}_${l}`}
									_class={{ other:'gui_small_icon' }}
									{...link}
								/>
							)
						})}
					</div>
				)}

				{(item.extra != undefined) && (
					<p class="extra">{item.extra}</p>
				)}

				{(item.description != undefined) && (
					<p class={`description${(not_overflowed) ? ' not_overflowed' : ''}`}>
						{item.description}

						{(what_is_it_link !== false) && (
							<div class="extra_header_link gui_not_in_top_small">
								<ActionLink
									_href={what_is_it_link}
									_target='_blank'
								>What is it?</ActionLink>
							</div>
						)}
					</p>
				)}

				{(item.description == undefined && what_is_it_link !== false) && (
					<div class="extra_header_link">
						<ActionLink
							_href={what_is_it_link}
							_target='_blank'
						>What is it?</ActionLink>
					</div>
				)}

				{(item.notice != undefined) && (
					<p class="notice" dangerouslySetInnerHTML={{ __html:item.notice }} />
				)}

				{(item.ribbons != undefined) && (
					<div>
						{item.ribbons.map((ribbons_group, r) => {
							let styles = null;
							if(r > 0) styles = { maxHeight:'85px' };

							return (
								<RibbonCollection
									key={`ribbon_group_${r}`}
									items={ribbons_group}
									style={styles}
									_class={{other:'gui_overflowed_auto gui_flex_center'}}
								/>
							)
						})}
					</div>
				)}

				{(props.actions != undefined) && (
					<ButtonCollection _class={{other:`gui_not_in_top_small ${props?.action_classes ?? ''}`}}>
						{props.actions(item.id)}
					</ButtonCollection>
				)}

				{(item.whole_actions != undefined) && (
					item.whole_actions
				)}
			</div>

			<FloatingExtraOptionsMenu
				actions={props.extra_actions}
				id={item.id}
				onToggleVisibility={setDisplayOptions}
			/>
		</Wrapper>
	)
};

const SingleCardEmpty = (props) => {
	const [ progress, setProgress ] = useState(false);
	const between_icon = props?.between_icon ?? false;

	const click = () => {
		//setProgress(true);
		if(props.empty_click != undefined) props.empty_click();
	};

	let main_classes = ['_card', 'empty', 'shadow_small'];
	if(between_icon !== false) main_classes.push(`${between_icon} between_icon`)

	return (
		<div class={main_classes.join(" ")} onClick={() => click()}>
			<div class="gui_pos_rel avatar_manager">
				<span class="account_initials gui_pos_abs">
					{(progress) ? <Spinner /> : '+'}
				</span>

				<p class="role" />
			</div>
			
			<div class="_info">
				<h4 class="title gui_force_roman">
					{(!progress) ? props.empty_label : props.progress_label}
				</h4>
			</div>
		</div>
	)
};

const AgentCards = ({ items, num_empty, ...props }) => {
	if(items == undefined) return null;
	if(num_empty == undefined) num_empty = [];

	const between_icon = props?.between_icon ?? false;
	const add_context = props?.add_context ?? false;

	useCSS('comp/agent_cards.css');

	let main_classes = ['cards_display'];
	if(add_context) main_classes.push('add_context');
	if(props._class != undefined) main_classes.push(props._class);

	const num_cols = props?.num_cols ?? 3;
	const container_styles = {
		gridTemplateColumns:(num_cols == 'auto') ? 'repeat(auto-fill, minmax(300px, 1fr))' : `repeat(${num_cols}, 1fr)`,
	};

	return (
		<div class={main_classes.join(" ")} style={props.style}>
			<div class="container" style={container_styles}>
				{items.map((item, i) => {
					const is_last = (i < items.length - 1) ? false : true;
					const enabled = item?.enabled ?? true;

					if(enabled !== true) return false;

					return (
						<SingleCard
							key={`card_${i}`}
							item={item}
							index={i}
							{...props}
							between_icon={(!is_last) ? between_icon : null}
						/>
					)
				})}

				{num_empty.map((item, j) => {
					const is_last = (j < num_empty.length - 1) ? false : true;

					return (
						<SingleCardEmpty
							key={`empty_card_${j}`}
							index={j}
							{...props}
							between_icon={(!is_last) ? between_icon : null}
						/>
					)
				})}

				{props.children}
			</div>
		</div>
	)
};

export default injectIntl(AgentCards);